import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/client';
import NotificationPopup from '../../component/NotificationPopup';
import EmailGuideModal from '../../component/EmailGuideModal';
import {
  Shield,
  Info,
  Eye,
  EyeOff,
  Save,
  Zap,
  HelpCircle,
  AlertCircle
} from 'lucide-react';

export default function EmailSettings({ companyId, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeProvider, setActiveProvider] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providersData, setProvidersData] = useState({
    gmail: {},
    microsoft: {},
    smtp: {}
  });
  const [formData, setFormData] = useState({
    email: '',
    from_name: '',
    client_id: '',
    client_secret: '',
    tenant_id: '',
    password: '',
    smtp_server: '',
    smtp_port: '587',
    smtp_username: '',
    use_tls: true,
    use_ssl: false,
    oauth_configured: false,
    password_configured: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [actualCompanyId, setActualCompanyId] = useState(companyId);
  const [guideModal, setGuideModal] = useState({ open: false, type: 'gmail' });
  const hasInitializedProvider = useRef(false);

  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    if (oauthSuccess) {
      setSnackbar({
        open: true,
        message: oauthSuccess === 'gmail'
          ? t('emailSettings.messages.gmailOAuthSuccess')
          : t('emailSettings.messages.microsoftOAuthSuccess'),
        severity: 'success',
      });
      setSearchParams({});
      if (actualCompanyId) fetchEmailSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, actualCompanyId]);

  useEffect(() => {
    if (!companyId) {
      fetchCompanyIdForAdmin();
    } else {
      setActualCompanyId(companyId);
      fetchEmailSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchCompanyIdForAdmin = async () => {
    try {
      const res = await api.get('companies/', {
        headers: { 'Authorization': localStorage.getItem('adminToken') },
      });
      const companies = res.data.results || res.data;
      if (Array.isArray(companies) && companies.length > 0) {
        setActualCompanyId(companies[0].id);
        fetchEmailSettings(companies[0].id);
      } else {
        setSnackbar({ open: true, message: 'No company found', severity: 'error' });
        setLoading(false);
      }
    } catch (err) {
      setSnackbar({ open: true, message: t('emailSettings.messages.failedToLoadCompany'), severity: 'error' });
      setLoading(false);
    }
  };

  const fetchEmailSettings = async (companyIdToUse = null) => {
    const idToUse = companyIdToUse || actualCompanyId;
    if (!idToUse) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`companies/${idToUse}/email-settings/`, {
        headers: { 'Authorization': localStorage.getItem('adminToken') },
      });
      const data = res.data;

      // Store all providers data
      if (data.providers) {
        setProvidersData(data.providers);
      }

      // Set active provider
      setActiveProvider(data.active_provider);

      const providerToShow = selectedProvider || data.active_provider || 'gmail';
      if (!hasInitializedProvider.current) {
        setSelectedProvider(providerToShow);
        hasInitializedProvider.current = true;
      }

      // Load selected provider's data
      const providerData = data.providers?.[providerToShow] || {};
      setFormData({
        email: providerData.email || '',
        from_name: providerData.from_name || '',
        client_id: providerData.client_id || '',
        client_secret: '', // Never show saved secret
        tenant_id: providerData.tenant_id || '',
        password: '', // Never show saved password
        smtp_server: providerData.smtp_server || '',
        smtp_port: providerData.smtp_port || '587',
        smtp_username: providerData.smtp_username || '',
        use_tls: providerData.use_tls !== undefined ? providerData.use_tls : true,
        use_ssl: providerData.use_ssl || false,
        oauth_configured: providerData.oauth_configured || false,
        password_configured: providerData.password_configured || false,
      });
    } catch (err) {
      console.error('📧 Error loading email settings:', err);
      if (err.response?.status !== 404) {
        setSnackbar({ open: true, message: 'Failed to load settings', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider) => {
    hasInitializedProvider.current = true;
    setSelectedProvider(provider);
    setErrors({});
    setShowPassword(false); // Reset password visibility when switching providers

    // Load saved data for the selected provider
    const providerData = providersData[provider] || {};
    setFormData({
      email: providerData.email || '',
      from_name: providerData.from_name || '',
      client_id: providerData.client_id || '',
      client_secret: '', // Clear secret field
      tenant_id: providerData.tenant_id || '',
      password: '', // Never show saved password
      smtp_server: providerData.smtp_server || '',
      smtp_port: providerData.smtp_port || '587',
      use_tls: providerData.use_tls !== undefined ? providerData.use_tls : true,
      use_ssl: providerData.use_ssl || false,
      oauth_configured: providerData.oauth_configured || false,
      password_configured: providerData.password_configured || false,
    });
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleOAuthConnect = async () => {
    let idToUse = actualCompanyId || companyId;

    // If company ID is not available, try to fetch it
    if (!idToUse) {
      try {
        const res = await api.get('companies/', {
          headers: { 'Authorization': localStorage.getItem('adminToken') },
        });
        const companies = res.data.results || res.data;
        if (Array.isArray(companies) && companies.length > 0) {
          idToUse = companies[0].id;
          setActualCompanyId(idToUse);
        } else {
          setSnackbar({ open: true, message: 'No company found', severity: 'error' });
          return;
        }
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to load company ID', severity: 'error' });
        return;
      }
    }

    if (!idToUse) {
      setSnackbar({ open: true, message: t('emailSettings.messages.companyIdNotFound'), severity: 'error' });
      return;
    }

    if (selectedProvider === 'microsoft') {
      if (!formData.client_id || !formData.client_secret || !formData.tenant_id) {
        setSnackbar({
          open: true,
          message: t('emailSettings.messages.saveFirst'),
          severity: 'error'
        });
        return;
      }

      setConnectingOAuth(true);
      try {
        const params = {
          client_id: formData.client_id,
          client_secret: formData.client_secret,
          tenant_id: formData.tenant_id,
        };

        const res = await api.get(`companies/${idToUse}/email-settings/oauth/initiate/${selectedProvider}/`, {
          headers: { 'Authorization': localStorage.getItem('adminToken') },
          params: params,
        });

        if (res.data.configured) {
          setSnackbar({
            open: true,
            message: res.data.message || t('emailSettings.messages.microsoftOAuthSuccess'),
            severity: 'success'
          });
          fetchEmailSettings();
        } else {
          setSnackbar({
            open: true,
            message: res.data.error || t('emailSettings.messages.failedToConfigureMicrosoft'),
            severity: 'error'
          });
        }
        setConnectingOAuth(false);
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || t('emailSettings.messages.failedToConfigureMicrosoft'),
          severity: 'error',
        });
        setConnectingOAuth(false);
      }
      return;
    }

    if (selectedProvider === 'gmail') {
      if (formData.client_id && !formData.client_secret) {
        setSnackbar({ open: true, message: t('emailSettings.messages.clientSecretRequired'), severity: 'error' });
        return;
      }

      setConnectingOAuth(true);
      try {
        const params = {};
        if (formData.client_id) params.client_id = formData.client_id;
        if (formData.client_secret) params.client_secret = formData.client_secret;

        const res = await api.get(`companies/${idToUse}/email-settings/oauth/initiate/${selectedProvider}/`, {
          headers: { 'Authorization': localStorage.getItem('adminToken') },
          params: params,
        });
        window.location.href = res.data.authorization_url;
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || t('emailSettings.messages.failedToInitiateOAuth'),
          severity: 'error',
        });
        setConnectingOAuth(false);
      }
    }
  };


  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = t('emailSettings.errors.emailRequired');

    if (selectedProvider === 'gmail' || selectedProvider === 'microsoft') {
      if (formData.client_id && !formData.client_secret && !formData.oauth_configured) {
        newErrors.client_secret = t('emailSettings.errors.clientSecretRequired');
      }
      if (selectedProvider === 'microsoft' && formData.client_id && !formData.tenant_id) {
        newErrors.tenant_id = t('emailSettings.errors.tenantIdRequired');
      }
    }

    if (selectedProvider === 'smtp') {
      // Password is only required if not already configured
      if (!formData.password && !formData.password_configured) {
        newErrors.password = t('emailSettings.errors.passwordRequired');
      }
      if (!formData.smtp_server) newErrors.smtp_server = t('emailSettings.errors.smtpServerRequired');
      if (!formData.smtp_port) newErrors.smtp_port = t('emailSettings.errors.smtpPortRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, skipValidation = false) => {
    if (e) e.preventDefault();
    if (!skipValidation && !validateForm()) return;

    setSaving(true);
    try {
      const idToUse = actualCompanyId || companyId;
      const dataToSend = {
        provider_type: selectedProvider,
        set_as_active: false, // Don't auto-activate on save
        email: formData.email,
        from_name: formData.from_name,
      };

      if (selectedProvider === 'gmail' || selectedProvider === 'microsoft') {
        if (formData.client_id) dataToSend.client_id = formData.client_id;
        if (formData.client_secret) dataToSend.client_secret = formData.client_secret;
        if (selectedProvider === 'microsoft' && formData.tenant_id) {
          dataToSend.tenant_id = formData.tenant_id;
        }
      } else if (selectedProvider === 'smtp') {
        // Only send password if user entered a new value
        if (formData.password) {
          dataToSend.password = formData.password;
        }
        dataToSend.smtp_server = formData.smtp_server;
        dataToSend.smtp_port = formData.smtp_port;
        if (formData.smtp_username) {
          dataToSend.smtp_username = formData.smtp_username;
        }
        dataToSend.use_tls = formData.use_tls;
        dataToSend.use_ssl = formData.use_ssl;
      }

      await api.put(`companies/${idToUse}/email-settings/`, dataToSend, {
        headers: { 'Authorization': localStorage.getItem('adminToken') },
      });

      setSnackbar({ open: true, message: t('emailSettings.messages.emailSettingsSaved'), severity: 'success' });
      await fetchEmailSettings(idToUse);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('emailSettings.messages.failedToSave'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleActivateProvider = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const idToUse = actualCompanyId || companyId;
      const dataToSend = {
        provider_type: selectedProvider,
        set_as_active: true, // Activate this provider
        email: formData.email,
        from_name: formData.from_name,
      };

      if (selectedProvider === 'gmail' || selectedProvider === 'microsoft') {
        if (formData.client_id) dataToSend.client_id = formData.client_id;
        if (formData.client_secret) dataToSend.client_secret = formData.client_secret;
        if (selectedProvider === 'microsoft' && formData.tenant_id) {
          dataToSend.tenant_id = formData.tenant_id;
        }
      } else if (selectedProvider === 'smtp') {
        // Only send password if user entered a new value
        if (formData.password) {
          dataToSend.password = formData.password;
        }
        dataToSend.smtp_server = formData.smtp_server;
        dataToSend.smtp_port = formData.smtp_port;
        if (formData.smtp_username) {
          dataToSend.smtp_username = formData.smtp_username;
        }
        dataToSend.use_tls = formData.use_tls;
        dataToSend.use_ssl = formData.use_ssl;
      }

      await api.put(`companies/${idToUse}/email-settings/`, dataToSend, {
        headers: { 'Authorization': localStorage.getItem('adminToken') },
      });

      const providerName = selectedProvider === 'gmail'
        ? t('emailSettings.messages.gmailActivated')
        : selectedProvider === 'microsoft'
          ? t('emailSettings.messages.microsoftActivated')
          : t('emailSettings.messages.smtpActivated');
      setSnackbar({ open: true, message: providerName, severity: 'success' });
      await fetchEmailSettings(idToUse);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('emailSettings.messages.failedToActivate'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0ea5e9]"></div>
      </div>
    );
  }

  const getActiveProviderName = () => {
    if (activeProvider === 'gmail') return t('emailSettings.providers.gmail');
    if (activeProvider === 'microsoft') return t('emailSettings.providers.microsoftFull');
    if (activeProvider === 'smtp') return t('emailSettings.providers.customSmtp');
    return t('emailSettings.providers.none');
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div
            className="w-full px-4 py-3 rounded-xl text-white shadow-sm"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white flex-shrink-0"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold mb-0.5 text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('emailSettings.title')}
                </h1>
                <p className="text-xs text-blue-100" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('emailSettings.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action / Active Provider Card (if present) */}
        {activeProvider && (
          <div className="bg-emerald-50/50 backdrop-blur-sm rounded-2xl border border-emerald-100/50 p-4 mb-6 shadow-sm flex items-center justify-between animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                <div className="absolute inset-0 w-3 h-3 bg-emerald-500 rounded-full animate-ping opacity-20"></div>
              </div>
              <div>
                <div className="font-bold text-gray-900 text-sm tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('emailSettings.activeSystem')}
                </div>
                <div className="text-xs text-gray-500 font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('emailSettings.currentlyRouting')} <span className="text-emerald-600 font-bold uppercase">{getActiveProviderName()}</span>
                </div>
              </div>
            </div>
            <div className="px-3 py-1 bg-white rounded-full border border-emerald-100 shadow-sm text-[10px] font-bold text-emerald-600 uppercase tracking-widest">
              Live
            </div>
          </div>
        )}

        <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 p-6 md:p-8">
          {/* Provider Tabs */}
          <div className="flex gap-2 mb-10 bg-gray-50/80 p-1.5 rounded-xl border border-gray-100 shadow-sm">
            {['gmail', 'microsoft', 'smtp'].map((provider) => {
              const getIcon = () => {
                switch (provider) {
                  case 'gmail':
                    return (
                      <svg className="w-4 h-4 transition-transform group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L12 9.573l8.073-6.08c1.618-1.214 3.927-.059 3.927 1.964z" />
                      </svg>
                    );
                  case 'microsoft':
                    return (
                      <svg className="w-4 h-4 transition-transform group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" />
                      </svg>
                    );
                  default:
                    return (
                      <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    );
                }
              };

              const getIconColor = () => {
                if (selectedProvider !== provider) return 'text-gray-400 group-hover:text-gray-600';
                switch (provider) {
                  case 'gmail': return 'text-[#ea4335]';
                  case 'microsoft': return 'text-[#00a4ef]';
                  default: return 'text-indigo-600';
                }
              };

              return (
                <button
                  key={provider}
                  onClick={() => handleProviderChange(provider)}
                  className={`flex-1 flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-lg font-bold text-sm transition-all duration-300 group ${selectedProvider === provider
                    ? 'bg-white text-gray-900 shadow-md ring-1 ring-gray-200'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  <span className={getIconColor()}>{getIcon()}</span>
                  {provider === 'gmail' ? t('emailSettings.providers.gmail') : provider === 'microsoft' ? t('emailSettings.providers.microsoft') : t('emailSettings.providers.custom')}
                </button>
              );
            })}
          </div>

          {/* OAuth 2.0 Settings Section */}
          <div className="mb-10">
            <div className="flex items-center justify-between pb-6 border-b border-gray-100 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#6366f1]/10 rounded-full flex items-center justify-center text-[#6366f1] shadow-sm ring-1 ring-[#6366f1]/20">
                  <Shield size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('emailSettings.oauthSettings')}
                  </h2>
                  <p className="text-xs text-gray-400 font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('emailSettings.version')} Engine
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-gray-50 p-2 pr-4 rounded-xl border border-gray-100">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {activeProvider === selectedProvider ? t('emailSettings.enabled') : t('emailSettings.disabled')}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    if (activeProvider === selectedProvider) {
                      setSaving(true);
                      try {
                        const idToUse = actualCompanyId || companyId;
                        await api.put(`companies/${idToUse}/email-settings/`, {
                          provider_type: selectedProvider,
                          enabled: false,
                          set_as_active: false,
                        }, {
                          headers: { 'Authorization': localStorage.getItem('adminToken') },
                        });
                        setSnackbar({ open: true, message: t('emailSettings.messages.providerDisabled'), severity: 'success' });
                        await fetchEmailSettings(idToUse);
                      } catch (err) {
                        setSnackbar({ open: true, message: err.response?.data?.error || t('emailSettings.messages.failedToDisable'), severity: 'error' });
                      } finally {
                        setSaving(false);
                      }
                    } else {
                      await handleActivateProvider();
                    }
                  }}
                  disabled={saving || connectingOAuth}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${activeProvider === selectedProvider ? 'bg-[#6366f1]' : 'bg-gray-300'
                    } ${saving || connectingOAuth ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${activeProvider === selectedProvider ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {/* Form Fields */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('emailSettings.fromEmail')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    className="w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                    required
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1 font-semibold">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('emailSettings.fromName')}
                  </label>
                  <input
                    type="text"
                    value={formData.from_name}
                    onChange={handleChange('from_name')}
                    placeholder={t('emailSettings.fromNamePlaceholder')}
                    className="w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                </div>
              </div>

              {(selectedProvider === 'gmail' || selectedProvider === 'microsoft') && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.clientId')}
                      </label>
                      <div className="relative group">
                        <input
                          type="text"
                          value={formData.client_id}
                          onChange={handleChange('client_id')}
                          placeholder={t('emailSettings.clientIdPlaceholder')}
                          className="w-full px-4 py-[14px] pr-10 rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 group-hover:text-blue-500 transition-colors pointer-events-none">
                          <Info size={18} />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.clientSecret')} {formData.client_id && <span className="text-red-500 font-bold">*</span>}
                      </label>
                      <div className="relative group">
                        <input
                          type="password"
                          value={formData.client_secret || ''}
                          onChange={handleChange('client_secret')}
                          placeholder={t('emailSettings.clientSecretPlaceholder')}
                          className="w-full px-4 py-[14px] pr-10 rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                          autoComplete="off"
                          onFocus={(e) => { if (e.target.dataset.eye !== 'true') e.target.type = 'password'; }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            const input = e.currentTarget.parentElement.querySelector('input');
                            if (input) {
                              input.dataset.eye = 'true';
                              input.type = input.type === 'password' ? 'text' : 'password';
                            }
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-blue-600 group-hover:text-blue-500 transition-all"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                      {errors.client_secret && <p className="text-red-500 text-xs mt-1 font-semibold">{errors.client_secret}</p>}
                    </div>
                  </div>

                  {selectedProvider === 'microsoft' && (
                    <div className="animate-in fade-in duration-300 slide-in-from-top-2">
                      <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.tenantId')} {formData.client_id && <span className="text-red-500 font-bold">*</span>}
                      </label>
                      <input
                        type="text"
                        value={formData.tenant_id}
                        onChange={handleChange('tenant_id')}
                        placeholder={t('emailSettings.tenantIdPlaceholder')}
                        className="w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      />
                      {errors.tenant_id && <p className="text-red-500 text-xs mt-1 font-semibold">{errors.tenant_id}</p>}
                    </div>
                  )}

                  <div className="pt-8 flex items-center justify-between border-t border-gray-100 mt-4">
                    <div 
                      onClick={() => setGuideModal({ open: true, type: selectedProvider })}
                      className="flex items-center gap-2 text-gray-400 hover:text-blue-500 transition-colors cursor-pointer group"
                    >
                      <HelpCircle size={16} className="text-gray-300 group-hover:text-blue-500" />
                      <span className="text-xs font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.needHelpConnecting', { provider: selectedProvider === 'gmail' ? 'Gmail' : 'Microsoft' })} <span className="text-blue-600 hover:underline">{t('emailSettings.viewGuide')}</span>
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={handleOAuthConnect}
                        disabled={connectingOAuth || (formData.client_id && !formData.client_secret) || (selectedProvider === 'microsoft' && formData.client_id && !formData.tenant_id)}
                        className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        <Zap size={16} className="text-blue-500" />
                        {connectingOAuth ? t('emailSettings.testing') : t('emailSettings.testConnection')}
                      </button>
                      <button
                        type="submit"
                        disabled={saving || connectingOAuth}
                        className="px-6 py-2.5 bg-[#6366f1] text-white rounded-xl font-bold text-sm hover:bg-[#4f46e5] hover:shadow-lg hover:shadow-indigo-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        <Save size={16} />
                        {saving ? t('emailSettings.saving') : t('emailSettings.saveChanges')}
                      </button>
                    </div>
                  </div>

                  {formData.oauth_configured && (
                    <div className="mt-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center gap-3 text-emerald-700 animate-in fade-in zoom-in duration-300">
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-sm ring-4 ring-emerald-500/20">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.oauthConnected')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedProvider === 'smtp' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('emailSettings.smtpPassword')} {!formData.password_configured && <span className="text-red-500 font-bold">*</span>}
                    </label>
                    {formData.password_configured && !formData.password ? (
                      <div className="relative group">
                        <input
                          type="text"
                          value="••••••••••••••••"
                          readOnly
                          disabled
                          className="w-full px-4 py-[14px] rounded-xl bg-emerald-50/50 border border-emerald-100 text-emerald-700 font-bold"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, password_configured: false, password: '' }))}
                          className="absolute right-4 top-1/2 -translate-y-1/2 px-4 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 rounded-lg font-bold transition-all border border-indigo-100 shadow-sm"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {t('emailSettings.change')}
                        </button>
                      </div>
                    ) : (
                      <div className="relative group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleChange('password')}
                          placeholder={formData.password_configured ? t('emailSettings.clientSecretPlaceholder') : t('emailSettings.smtpPassword')}
                          className="w-full px-4 py-[14px] pr-10 rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                          required={!formData.password_configured}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowPassword(!showPassword);
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-blue-600 group-hover:text-blue-500 transition-all"
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    )}
                    {errors.password && <p className="text-red-500 text-xs mt-1 font-semibold">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('emailSettings.smtpUsername')} <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider ml-1">({t('common.optional')})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.smtp_username}
                      onChange={handleChange('smtp_username')}
                      placeholder={t('emailSettings.smtpUsernamePlaceholder')}
                      className="w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    />
                    <div className="flex items-center gap-1.5 mt-2">
                      <AlertCircle size={12} className="text-gray-400" />
                      <p className="text-gray-400 text-[10px] font-bold tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.smtpUsernameHelp')}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.smtpServer')} <span className="text-red-500 font-bold">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.smtp_server}
                        onChange={handleChange('smtp_server')}
                        placeholder={t('emailSettings.smtpServerPlaceholder')}
                        className="w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                        required
                      />
                      {errors.smtp_server && <p className="text-red-500 text-xs mt-1 font-semibold">{errors.smtp_server}</p>}
                    </div>

                    <div>
                      <label className="block text-[13px] font-bold text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('emailSettings.smtpPort')} <span className="text-red-500 font-bold">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.smtp_port}
                        onChange={handleChange('smtp_port')}
                        className="w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-bold placeholder:text-gray-400"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                        required
                      />
                      {errors.smtp_port && <p className="text-red-500 text-xs mt-1 font-semibold">{errors.smtp_port}</p>}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-8 py-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.use_tls}
                          onChange={handleChange('use_tls')}
                          className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-300 transition-all checked:bg-indigo-600 checked:border-indigo-600"
                        />
                        <svg className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-bold text-gray-600 group-hover:text-gray-900 transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('emailSettings.useTls')}</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.use_ssl}
                          onChange={handleChange('use_ssl')}
                          className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-300 transition-all checked:bg-indigo-600 checked:border-indigo-600"
                        />
                        <svg className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-bold text-gray-600 group-hover:text-gray-900 transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('emailSettings.useSsl')}</span>
                    </label>
                  </div>

                  <div className="pt-8 flex items-center justify-end border-t border-gray-100 mt-4">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                      <button
                        type="submit"
                        disabled={saving || connectingOAuth}
                        className="w-full md:w-auto px-10 py-3 bg-[#6366f1] text-white rounded-xl font-bold text-sm hover:bg-[#4f46e5] hover:shadow-lg hover:shadow-indigo-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        <Save size={18} />
                        {saving ? t('emailSettings.saving') : t('emailSettings.saveChanges')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Info */}
              <div className="flex items-start gap-2 pt-2">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-xs text-gray-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('emailSettings.securityInfo')}
                </p>
              </div>

              {/* Action Buttons for SMTP */}
              {selectedProvider === 'smtp' && (
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={saving || connectingOAuth}
                    className="w-full px-6 py-3 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98]"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)"
                    }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {saving ? t('emailSettings.saving') : t('emailSettings.saveChanges')}
                  </button>
                </div>
              )}

            </form>
          </div>
        </div>
      </div>

      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />

      <EmailGuideModal 
        isOpen={guideModal.open}
        onClose={() => setGuideModal(prev => ({ ...prev, open: false }))}
        type={guideModal.type}
      />
    </div>
  );
}
